import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  FolderKanban,
  Plus,
  Search,
  Eye,
  MoreVertical,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Projects() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchProjects();
  }, [statusFilter]);

  const fetchProjects = async () => {
    try {
      let url = "/projects";
      if (statusFilter !== "all") url += `?status=${statusFilter}`;
      const response = await apiClient.get(url);
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name?.toLowerCase().includes(query) ||
      project.client_name?.toLowerCase().includes(query)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStageProgress = (stages) => {
    if (!stages || stages.length === 0) return 0;
    const completed = stages.filter(s => s.status === 'completed').length;
    return Math.round((completed / stages.length) * 100);
  };

  const getCurrentStage = (stages) => {
    if (!stages || stages.length === 0) return null;
    const inProgress = stages.find(s => s.status === 'in_progress');
    if (inProgress) return inProgress;
    const pending = stages.find(s => s.status === 'pending');
    return pending || stages[stages.length - 1];
  };

  return (
    <div className="page-content" data-testid="projects-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Track and manage all your active projects</p>
        </div>
        <Link to="/clients">
          <Button className="bg-[#FF4500] hover:bg-[#E65100] btn-glow">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#171717] border-[#262626]"
            data-testid="search-projects"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-[#171717] border-[#262626]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-64 bg-[#1A1A1A] rounded animate-pulse"></div>
          ))}
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="projects-grid">
          {filteredProjects.map((project) => {
            const currentStage = getCurrentStage(project.stages);
            const progress = getStageProgress(project.stages);
            
            return (
              <div
                key={project.project_id}
                className="bg-[#1A1A1A] border border-[#262626] rounded overflow-hidden card-hover relative group"
                data-testid={`project-card-${project.project_id}`}
              >
                {/* Header */}
                <div className="p-5 border-b border-[#262626]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Link to={`/projects/${project.project_id}`}>
                        <h3 className="font-semibold text-white text-lg hover:text-[#FF4500] transition-colors">
                          {project.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Users className="w-3 h-3" />
                        {project.client_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge type-${project.project_type}`}>
                        {project.project_type}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded hover:bg-[#262626]">
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#262626]">
                          <DropdownMenuItem asChild>
                            <Link to={`/projects/${project.project_id}`} className="flex items-center gap-2 cursor-pointer">
                              <Eye className="w-4 h-4" /> View Details
                            </Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>

                {/* Stage Progress */}
                <div className="p-5">
                  <div className="flex justify-between text-xs text-gray-500 mb-2">
                    <span>Progress</span>
                    <span>{progress}%</span>
                  </div>
                  
                  {/* Stage Boxes */}
                  <div className="flex gap-1 mb-4">
                    {project.stages?.map((stage, idx) => (
                      <div
                        key={idx}
                        className={`relative flex-1 aspect-square max-h-16 rounded flex items-center justify-center ${
                          stage.status === 'completed' ? 'bg-[#10B981]/20 border border-[#10B981]/40' :
                          stage.status === 'in_progress' ? 'bg-[#FF4500]/20 border border-[#FF4500]/40' :
                          'bg-[#262626] border border-[#333]'
                        }`}
                        title={stage.stage_name}
                      >
                        <span className={`font-bold text-lg ${
                          stage.status === 'completed' ? 'text-[#10B981]/60' :
                          stage.status === 'in_progress' ? 'text-[#FF4500]/60' :
                          'text-gray-600'
                        }`}>
                          {String(stage.stage_number).padStart(2, '0')}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Current Stage */}
                  {currentStage && (
                    <div className="p-3 bg-[#171717] rounded border border-[#262626]">
                      <p className="text-xs text-gray-500 mb-1">Current Stage</p>
                      <p className="text-sm font-medium text-white">{currentStage.stage_name}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-[#171717] border-t border-[#262626] flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total Fee</p>
                    <p className="text-sm font-semibold text-white">{formatCurrency(project.total_fee)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Site Visits</p>
                    <p className="text-sm font-semibold text-white">
                      {project.site_visits_used || 0} / {project.site_visits_included}
                    </p>
                  </div>
                  <span className={`badge ${
                    project.status === 'active' ? 'status-active' :
                    project.status === 'completed' ? 'status-completed' :
                    project.status === 'on_hold' ? 'status-pending' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state" data-testid="empty-state">
          <div className="empty-state-icon">
            <FolderKanban />
          </div>
          <h3 className="empty-state-title">No projects found</h3>
          <p className="empty-state-description">
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Create a project from a client to get started"}
          </p>
          <Link to="/clients">
            <Button className="bg-[#FF4500] hover:bg-[#E65100]">
              View Clients
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
